/**
 * filterReset.js — ONE source of truth for resetting listing-page filters.
 *
 * Both Home.js and Shop.js call resetAllListingFilters(...) with the setters
 * they actually use. Any piece of filter state that doesn't apply to a given
 * page is simply omitted from `setters` — the helper silently skips it.
 *
 * What gets wiped:
 *  1. Unified filters object (homeFilters / shopFilters)
 *  2. Legacy individual filter state (sortBy, priceRange, locationFilter,
 *     countryFilter, cityFilter, cityFilterActive, showDistance, buyerLocation)
 *  3. Category / subcategory drill-down
 *  4. Search query
 *  5. Discovery / image-search state (discoveryMode, forYouActive,
 *     isImageSearchActive, imageSearchResults)
 *  6. URL search params (category, search, country, city, priceMin, priceMax, sort)
 *  7. sessionStorage page-view cache (wassla.page.view.state.v1.<pageKey>)
 *  8. sessionStorage buyerLocation cache
 *  9. Paging (page index)
 * 10. Sheet visibility (showFilters)
 */

export const DEFAULT_LISTING_FILTERS = {
  country: '',
  city: '',
  priceMin: '',
  priceMax: '',
  sort: 'newest',
};

const URL_FILTER_KEYS = [
  'category',
  'search',
  'country',
  'city',
  'priceMin',
  'priceMax',
  'sort',
  'subcategory',
];

const PAGE_VIEW_STATE_PREFIX = 'wassla.page.view.state.v1.';

/**
 * Reset every piece of filter state for a listing page.
 *
 * @param {Object} opts
 * @param {Object} opts.setters
 *   React state setters. Pass only the ones the page actually uses.
 *     setFilters           — setShopFilters / setHomeFilters
 *     setPriceRange
 *     setLocationFilter
 *     setCountryFilter
 *     setCityFilter
 *     setCityFilterActive
 *     setShowDistance
 *     setBuyerLocation
 *     setSortBy
 *     setSelectedCategory
 *     setSelectedSubcategory
 *     setSearchQuery
 *     setDiscoveryMode
 *     setForYouActive
 *     setImageSearchResults
 *     setIsImageSearchActive
 *     setPage
 *     setShowFilters
 * @param {string} [opts.pageKey]  sessionStorage page key ("home" | "shop")
 * @param {URLSearchParams} [opts.searchParams]
 * @param {Function} [opts.setSearchParams]
 * @param {Object}   [opts.defaults]  override DEFAULT_LISTING_FILTERS
 */
export function resetAllListingFilters({
  setters = {},
  pageKey,
  searchParams,
  setSearchParams,
  defaults = DEFAULT_LISTING_FILTERS,
}) {
  const s = setters;

  // 1. Unified filters object
  s.setFilters?.({ ...defaults });

  // 2. Legacy individual filter state
  s.setPriceRange?.({ min: '', max: '' });
  s.setLocationFilter?.('');
  s.setCountryFilter?.('');
  s.setCityFilter?.('');
  s.setCityFilterActive?.(false);
  s.setShowDistance?.(false);
  s.setBuyerLocation?.(null);
  s.setSortBy?.('newest');

  // 3. Category drill-down
  s.setSelectedCategory?.('all');
  s.setSelectedSubcategory?.(null);

  // 4. Search query
  s.setSearchQuery?.('');

  // 5. Discovery / image-search state
  s.setDiscoveryMode?.('');
  s.setForYouActive?.(false);
  s.setImageSearchResults?.([]);
  s.setIsImageSearchActive?.(false);

  // 6. URL search params — strip every filter key, preserve anything else
  if (searchParams && setSearchParams) {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    URL_FILTER_KEYS.forEach((k) => {
      if (next.has(k)) {
        next.delete(k);
        changed = true;
      }
    });
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }

  // 7. sessionStorage page-view cache
  if (pageKey && typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(`${PAGE_VIEW_STATE_PREFIX}${pageKey}`);
    } catch {
      // ignore
    }
  }

  // 8. sessionStorage buyerLocation cache (Shop distance toggle)
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem('buyerLocation');
    } catch {
      // ignore
    }
  }

  // 9. Reset paging
  s.setPage?.(1);

  // 10. Close the filter sheet
  s.setShowFilters?.(false);
}

export default resetAllListingFilters;
